import {
  getObjectivesWaitlistSignup,
  joinObjectivesWaitlist,
} from "@/api/privateRequests/user/objectivesWaitlist";
import { trackObjectivesWaitlistJoin } from "@/events/landingPagesMixpanel";
import { AppDispatch } from "@/redux/store";
import { getUserSelector, updateUser } from "@/redux/user/userSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

export type WaitlistForm = "hero" | "bottom";

export type WaitlistState =
  | "unknown"
  | "notJoined"
  | "joining"
  | "joined"
  | "alreadyJoined"
  | "error";

// Shared with the form's own validation, so a prefilled address faces the same check.
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Campaign links carry the recipient's address so they don't have to retype it.
const EMAIL_PARAM = "email";

// RFC 5321's limit; longer than this is junk, not an address.
const EMAIL_MAX_LENGTH = 254;

/** The `?email=` prefill, or "" when absent or unusable. Note `+` arrives as a space. */
export const prefillFromParam = (param: string | null): string => {
  const email = param?.trim() ?? "";
  if (email.length > EMAIL_MAX_LENGTH) return "";
  return EMAIL_PATTERN.test(email) ? email : "";
};

// Remembers an anonymous signup on this device; accounts ask the backend.
const JOINED_KEY = "objectivesWaitlistJoined";

const rememberedJoin = () => {
  try {
    return localStorage.getItem(JOINED_KEY) === "true";
  } catch {
    return false;
  }
};

const rememberJoin = () => {
  try {
    localStorage.setItem(JOINED_KEY, "true");
  } catch {
    // private browsing, storage disabled
  }
};

/** Shared by both forms, so joining through one updates the other. */
export const useObjectivesWaitlist = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);
  const uuid = user.uuid;
  const [state, setState] = useState<WaitlistState>("unknown");
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once on arrival: the param is stripped below, and re-reading it would
  // overwrite whatever the visitor has typed since.
  const [prefillEmail] = useState(() =>
    prefillFromParam(searchParams.get(EMAIL_PARAM)),
  );

  // An address left in the URL reaches Mixpanel's $current_url and Sentry's
  // request URL, so drop it from the bar once read. The utm_* params stay.
  useEffect(() => {
    if (!searchParams.has(EMAIL_PARAM)) return;
    const stripped = new URLSearchParams(searchParams);
    stripped.delete(EMAIL_PARAM);
    setSearchParams(stripped, { replace: true });
  }, [searchParams, setSearchParams]);

  // Only an account can be looked up; anonymous visitors fall back to this device.
  useEffect(() => {
    if (!uuid) {
      setState(rememberedJoin() ? "alreadyJoined" : "notJoined");
      return;
    }
    let cancelled = false;
    getObjectivesWaitlistSignup()
      .then((signup) => {
        if (cancelled) return;
        // A join that finished first wins over this older read.
        setState((s) =>
          s === "joining" || s === "joined"
            ? s
            : signup
              ? "alreadyJoined"
              : "notJoined",
        );
      })
      .catch(() => !cancelled && setState("notJoined"));
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  const submit = async (form: WaitlistForm, email: string) => {
    setState("joining");
    try {
      await joinObjectivesWaitlist(uuid ? user.email : email);
      // The form's consent box is required, so joining opts in to email.
      if (uuid && !user.emailNotification) {
        dispatch(updateUser({ emailNotification: true }));
      }
      rememberJoin();
      trackObjectivesWaitlistJoin(form);
      setState("joined");
    } catch {
      setState("error");
    }
  };

  return { state, loggedIn: !!uuid, prefillEmail, submit };
};
